import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Alert from "@mui/material/Alert";
import axios from "axios";
import API_ENDPOINTS from "config/api";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function Transferencias() {
  const [transferencias, setTransferencias] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [productos, setProductos] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [transferenciaForm, setTransferenciaForm] = useState({
    almacen_origen_id: "",
    almacen_destino_id: "",
    producto_id: "",
    cantidad: "",
    fecha_envio: "",
    fecha_recepcion: "",
    estado: "pendiente",
    motivo: "",
    observaciones: "",
    transportista: "",
    guia_transporte: "",
  });

  // const endpoint = ... (removed)
  const empresaId = localStorage.getItem("empresaId");

  const steps = ["Datos de Transferencia", "Transporte", "Confirmación"];

  const estadosTransferencia = [
    { value: "pendiente", label: "Pendiente", color: "warning" },
    { value: "en_transito", label: "En Tránsito", color: "info" },
    { value: "recibido", label: "Recibido", color: "success" },
    { value: "cancelado", label: "Cancelado", color: "error" },
  ];

  useEffect(() => {
    fetchTransferencias();
    fetchProductos();
    fetchAlmacenes();
  }, [empresaId]);

  const fetchTransferencias = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.INVENTARIO.TRANSFERENCIAS, {
        params: { empresa_id: empresaId },
      });
      setTransferencias(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener transferencias:", error);
      setTransferencias([]);
    }
  };

  const fetchProductos = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.INVENTARIO.GENERAL, {
        params: { empresa_id: empresaId },
      });
      setProductos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener productos:", error);
      setProductos([]);
    }
  };

  const fetchAlmacenes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ALMACENES, {
        params: { empresa_id: empresaId },
      });
      setAlmacenes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener almacenes:", error);
      setAlmacenes([]);
    }
  };

  const handleOpenDialog = () => {
    setTransferenciaForm({
      almacen_origen_id: "",
      almacen_destino_id: "",
      producto_id: "",
      cantidad: "",
      fecha_envio: new Date().toISOString().split("T")[0],
      fecha_recepcion: "",
      estado: "pendiente",
      motivo: "",
      observaciones: "",
      transportista: "",
      guia_transporte: "",
    });
    setActiveStep(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTransferenciaForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    if (!transferenciaForm.almacen_origen_id || !transferenciaForm.almacen_destino_id || !transferenciaForm.producto_id || !transferenciaForm.cantidad) {
      alert("Por favor complete los campos obligatorios: Almacén Origen, Almacén Destino, Producto y Cantidad");
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...transferenciaForm,
        empresa_id: localStorage.getItem("empresaId") || 1,
        producto_id: parseInt(transferenciaForm.producto_id),
        almacen_origen_id: parseInt(transferenciaForm.almacen_origen_id),
        almacen_destino_id: parseInt(transferenciaForm.almacen_destino_id),
        cantidad: parseFloat(transferenciaForm.cantidad),
      };

      await axios.post(API_ENDPOINTS.INVENTARIO.TRANSFERENCIAS, data);
      alert("Transferencia registrada exitosamente");
      fetchTransferencias();
      handleCloseDialog();
    } catch (error) {
      console.error("Error al registrar transferencia:", error);
      const errorMsg = error.response?.data?.message || error.message;
      alert("Error al registrar la transferencia: " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEstado = async (id, nuevoEstado) => {
    try {
      await axios.patch(API_ENDPOINTS.INVENTARIO.TRANSFERENCIA(id), {
        estado: nuevoEstado,
      });
      fetchTransferencias();
    } catch (error) {
      console.error("Error al actualizar estado:", error);
    }
  };

  const getEstadoChip = (estado) => {
    const estadoObj = estadosTransferencia.find((e) => e.value === estado);
    return (
      <Chip label={estadoObj?.label || estado} color={estadoObj?.color || "default"} size="small" />
    );
  };

  const columns = [
    { Header: "Fecha", accessor: "fecha", width: "10%", align: "left" },
    { Header: "Producto", accessor: "producto", align: "left" },
    { Header: "Origen", accessor: "origen", width: "15%", align: "left" },
    { Header: "Destino", accessor: "destino", width: "15%", align: "left" },
    { Header: "Cantidad", accessor: "cantidad", width: "10%", align: "center" },
    { Header: "Estado", accessor: "estado", width: "12%", align: "center" },
    { Header: "Acciones", accessor: "acciones", width: "15%", align: "center" },
  ];

  const rows = transferencias.map((transferencia) => ({
    fecha: new Date(transferencia.fecha_envio).toLocaleDateString(),
    producto: transferencia.producto?.nombre || "N/A",
    origen: transferencia.almacen_origen?.nombre || "N/A",
    destino: transferencia.almacen_destino?.nombre || "N/A",
    cantidad: `${transferencia.cantidad} ${transferencia.producto?.unidad_medida || ""}`,
    estado: getEstadoChip(transferencia.estado),
    acciones: (
      <MDBox display="flex" gap={1} justifyContent="center">
        {transferencia.estado === "pendiente" && (
          <Button
            size="small"
            variant="contained"
            color="info"
            onClick={() => handleUpdateEstado(transferencia.id, "en_transito")}
          >
            Enviar
          </Button>
        )}
        {transferencia.estado === "en_transito" && (
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => handleUpdateEstado(transferencia.id, "recibido")}
          >
            Recibir
          </Button>
        )}
      </MDBox>
    ),
  }));

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Almacén Origen"
                name="almacen_origen_id"
                value={transferenciaForm.almacen_origen_id}
                onChange={handleInputChange}
                required
              >
                {almacenes.map((alm) => (
                  <MenuItem key={alm.id} value={alm.id}>
                    {alm.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Almacén Destino"
                name="almacen_destino_id"
                value={transferenciaForm.almacen_destino_id}
                onChange={handleInputChange}
                required
              >
                {almacenes
                  .filter((alm) => alm.id !== parseInt(transferenciaForm.almacen_origen_id))
                  .map((alm) => (
                    <MenuItem key={alm.id} value={alm.id}>
                      {alm.nombre}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                select
                label="Producto"
                name="producto_id"
                value={transferenciaForm.producto_id}
                onChange={handleInputChange}
                required
              >
                {productos.map((prod) => (
                  <MenuItem key={prod.id} value={prod.id}>
                    {prod.nombre} - Stock: {prod.stock_actual}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cantidad"
                name="cantidad"
                type="number"
                value={transferenciaForm.cantidad}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Motivo de Transferencia"
                name="motivo"
                value={transferenciaForm.motivo}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Transportista"
                name="transportista"
                value={transferenciaForm.transportista}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Guía de Transporte"
                name="guia_transporte"
                value={transferenciaForm.guia_transporte}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de Envío"
                name="fecha_envio"
                value={transferenciaForm.fecha_envio}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Estimada Recepción"
                name="fecha_recepcion"
                value={transferenciaForm.fecha_recepcion}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observaciones"
                name="observaciones"
                value={transferenciaForm.observaciones}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        );
      case 2:
        const almacenOrigen = almacenes.find(
          (a) => a.id === parseInt(transferenciaForm.almacen_origen_id)
        );
        const almacenDestino = almacenes.find(
          (a) => a.id === parseInt(transferenciaForm.almacen_destino_id)
        );
        const producto = productos.find((p) => p.id === parseInt(transferenciaForm.producto_id));
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">
                <strong>Resumen de Transferencia</strong>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant="body2">
                <strong>Producto:</strong> {producto?.nombre}
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Cantidad:</strong> {transferenciaForm.cantidad} {producto?.unidad_medida}
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Origen:</strong> {almacenOrigen?.nombre}
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Destino:</strong> {almacenDestino?.nombre}
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Fecha de Envío:</strong> {transferenciaForm.fecha_envio}
              </MDTypography>
              {transferenciaForm.transportista && (
                <MDTypography variant="body2">
                  <strong>Transportista:</strong> {transferenciaForm.transportista}
                </MDTypography>
              )}
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h6">Transferencias entre Almacenes</MDTypography>
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<Icon>local_shipping</Icon>}
          onClick={handleOpenDialog}
        >
          Nueva Transferencia
        </MDButton>
      </MDBox>

      <Card>
        <MDBox>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </MDBox>
      </Card>

      {/* Dialog para crear transferencia */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Nueva Transferencia de Inventario</DialogTitle>
        <DialogContent>
          <MDBox sx={{ mt: 2 }}>
            <Stepper activeStep={activeStep}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <MDBox sx={{ mt: 3 }}>{renderStepContent(activeStep)}</MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <MDBox sx={{ flex: "1 1 auto" }} />
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Atrás
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
              {loading ? "Creando..." : "Crear Transferencia"}
            </Button>
          ) : (
            <Button onClick={handleNext} variant="contained">
              Siguiente
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
